/* global __APP_VERSION__, __BUILD_TIME__ */
/**
 * Environment configuration for the application
 *
 * Set VITE_MONAMI_HOST to override the default API host.
 * See README.md for examples.
 */

const DEFAULT_HOST = 'https://app.monami.test';

export const MONAMI_HOST = import.meta.env.VITE_MONAMI_HOST || DEFAULT_HOST;

/**
 * Build identity. `APP_VERSION` is the git commit the bundle was built from
 * (CI passes it as VITE_APP_VERSION; local builds read the working tree),
 * `BUILD_TIME` the ISO timestamp of the build, `APP_ENVIRONMENT` the deploy
 * target (development, demo, production) or the Vite mode locally.
 */
export const APP_VERSION: string = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown';
export const BUILD_TIME: string | null = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : null;
export const APP_ENVIRONMENT: string = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;

/** Short form of APP_VERSION for display: a full commit sha is cut to 7 characters. */
export function shortAppVersion(version: string = APP_VERSION): string {
  return /^[0-9a-f]{40}$/i.test(version) ? version.slice(0, 7) : version;
}
export const MONAMI_WWW_HOST = MONAMI_HOST.replace('app.', 'www.');

/** Help Center article on installing Mon Ami GO to the device. */
export const INSTALL_GUIDE_URL = 'https://monamisupport.zendesk.com/hc/en-us/articles/49435084350491-How-to-Download-Mon-Ami-GO';

/**
 * When true, disables biometric registration and lock screen functionality.
 * This is useful for E2E testing where WebAuthn cannot be properly mocked.
 * Set via VITE_DISABLE_BIOMETRICS=true at build time.
 */
export const BIOMETRICS_DISABLED = import.meta.env.VITE_DISABLE_BIOMETRICS === 'true';

/**
 * When true, disables analytics event tracking and syncing.
 * Set via VITE_DISABLE_ANALYTICS=true at build time.
 */
export const ANALYTICS_DISABLED = import.meta.env.VITE_DISABLE_ANALYTICS === 'true';

/**
 * When true, Sentry Session Replay is paused while a document is being
 * edited (it stays on for every other page). Replay's MutationObserver
 * serializes every DOM change, and large SuperForm documents can make each
 * answer visibly slower with it on. Set via
 * VITE_DISABLE_REPLAY_IN_DOCUMENT_EDITOR=true at build time (a GitHub
 * environment variable in the deploy workflow).
 */
export const REPLAY_DISABLED_IN_DOCUMENT_EDITOR = import.meta.env.VITE_DISABLE_REPLAY_IN_DOCUMENT_EDITOR === 'true';

/**
 * Offline time limit in days before access is blocked.
 * Default is 30 days in production.
 *
 * For local testing, you can override this with:
 * VITE_OFFLINE_LIMIT_DAYS=0.000694 (1 minute)
 * VITE_OFFLINE_LIMIT_DAYS=0.00347 (5 minutes)
 * VITE_OFFLINE_LIMIT_DAYS=0.0208 (30 minutes)
 * VITE_OFFLINE_LIMIT_DAYS=0.0417 (1 hour)
 *
 * Set at build time or in .env.local file.
 */
const parsedOfflineLimitDays = import.meta.env.VITE_OFFLINE_LIMIT_DAYS
  ? parseFloat(import.meta.env.VITE_OFFLINE_LIMIT_DAYS)
  : 30;
export const OFFLINE_LIMIT_DAYS = Number.isFinite(parsedOfflineLimitDays) && parsedOfflineLimitDays > 0
  ? parsedOfflineLimitDays
  : 30;

// API endpoints
export const API_ENDPOINTS = {
  clients: `${MONAMI_HOST}/api/offline/clients`,
  documentTemplates: `${MONAMI_HOST}/api/offline/document_templates`,
  programs: `${MONAMI_HOST}/api/offline/programs`,
  sites: `${MONAMI_HOST}/api/offline/sites`,
  noteOptions: `${MONAMI_HOST}/api/offline/note_options`,
  analytics: `${MONAMI_HOST}/api/offline/analytics`,
  help: `${MONAMI_HOST}/api/offline/help`,
  devices: `${MONAMI_HOST}/api/offline/devices`,
  devicesRefresh: `${MONAMI_HOST}/api/offline/devices/refresh`,
  devicesCurrent: `${MONAMI_HOST}/api/offline/devices/current`,
  currentUser: `${MONAMI_HOST}/api/offline/current_user`,
};

